import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
  Table,
  Badge,
  ButtonGroup,
  InputGroup,
  Nav,
  Alert,
} from "react-bootstrap";
import PageTitle from "../components/common/PageTitle";
import api from "../api/api";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "../admin/AdminManagement.css";
import "./SensorInstallationManager.css";

const SensorInstallationManager = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Data states
  const [sensors, setSensors] = useState([]);
  const [fields, setFields] = useState([]);
  const [farms, setFarms] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [users, setUsers] = useState([]);
  const [zones, setZones] = useState([]);

  // View mode: 'sensors' or 'fields'
  const [viewMode, setViewMode] = useState("sensors");

  // Filter states - Simplified
  const [filters, setFilters] = useState({
    farm: "",
    field: "",
    status: "all",
    search: "",
    fieldsWithSensors: false
  });

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [sensorHistory, setSensorHistory] = useState([]);
  const [assignData, setAssignData] = useState({
    sensor_id: null,
    user_id: "",
    farm_id: "",
    field_id: "",
    zone_id: "",
    installation_date: new Date().toISOString().split("T")[0],
  });

  // Loading & Pagination
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Fetch functions
  const fetchUsers = async () => {
    try {
      const response = await api.get("/admin/users");
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };
const fetchZones = async () => {
  try {
    const response = await api.get("/admin/fields");
    const fieldsData = response.data.fields || []; // Use the 'fields' array, not 'x'
    const allZones = [];

    fieldsData.forEach((field) => {
      if (field.zones && Array.isArray(field.zones)) {
        field.zones.forEach((zone) => {
          allZones.push({
            ...zone,
            field_id: field.id,
            farm_id: field.farm_id,
            field_name: field.name,
            farm_name: field.farm?.name || "",
          });
        });
      }
    });

    setZones(allZones);
  } catch (error) {
    console.error("Error fetching zones:", error);
    setZones([]);
  }
};
  const fetchFarms = async () => {
    try {
      const response = await api.get("/admin/all-farms");
      const farmsData = response.data.farms || [];
      setFarms(farmsData);
    } catch (error) {
      console.error("Error fetching farms:", error);
    }
  };

  const fetchFields = async () => {
    try {
      const response = await api.get("/admin/fields");
      const allFields = response.data.fields;
      setFields(allFields);
    } catch (error) {
      console.error("Error fetching fields:", error);
    }
  };

  const fetchSensors = async () => {
    try {
      const response = await api.get("/admin/all-sensors");
      const sensorsData = response.data?.sensors || {};
      const sensorsArray = Object.values(sensorsData).map((item) => ({
        id: item.sensor?.id,
        uid: item.sensor?.uid,
        code: item.sensor?.code,
        sensorName: item.sensor?.sensorName,
        field_id: item.sensor?.field_id,
        zone_id: item.sensor?.zone_id,
        created_at: item.sensor?.created_at,
        Latitude: item.sensor?.Latitude,
        Longitude: item.sensor?.Longitude,
        user_id: item.sensor?.user_id,
        is_deleted: item.sensor?.is_deleted,
        temperature: item.data?.temperature,
        humidity: item.data?.humidity,
        pressure: item.data?.pressure,
        charge: item.data?.charge,
        signal: item.data?.signal,
        lastReadingTime: item.data?.time,
        frequence: item.config?.frequence,
        sensor: item.sensor,
        data: item.data,
        config: item.config,
      }));

      setSensors(sensorsArray);
    } catch (error) {
      console.error("Error fetching sensors:", error);
      setSensors([]);
    }
  };

  const fetchInstallations = async () => {
    try {
      const response = await api.get("/sensor-installation/all");
      setInstallations(response.data.installations || []);
    } catch (error) {
      console.error("Error fetching installations:", error);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchFields(),
      fetchSensors(),
      fetchInstallations(),
      fetchUsers(),
      fetchZones(),
      fetchFarms(),
    ]).finally(() => setLoading(false));
  }, []);

  // Helper functions
  const getFilteredFarms = () => {
    if (!assignData.user_id) return [];
    const userId = Number(assignData.user_id);
    const userFarms = farms.filter((farm) => Number(farm.user_id) === userId);
    if (userFarms.length === 0) {
      return farms;
    }
    return userFarms;
  };

// Filter fields based on search, farm, and active sensors
const getFilteredFields = () => {
  let filtered = [...fields];

  // Search filter (by field name or farm name)
  if (filters.search) {
    filtered = filtered.filter(
      (field) =>
        (field.name || "")
          .toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        (field.farm?.name || "")
          .toLowerCase()
          .includes(filters.search.toLowerCase())
    );
  }

  // Farm filter
  if (filters.farm) {
    filtered = filtered.filter((field) => field.farm_id === filters.farm);
  }

  // Filter to show only fields with active sensors
  if (filters.fieldsWithSensors) {
    filtered = filtered.filter((field) => {
      const fieldSensors = getFieldSensors(field.id);
      const activeSensors = fieldSensors.filter((fs) => !fs.removal_date);
      return activeSensors.length > 0;
    });
  }

  return filtered;
};

const getFilteredZones = () => {
  if (!assignData.field_id) return [];
  // Convert both to strings for comparison
  return zones.filter((zone) => String(zone.field_id) === String(assignData.field_id));
};    

  const getCurrentAssignment = (sensorId) => {
    return installations.find(
      (inst) => inst.sensor_id === sensorId && !inst.removal_date
    );
  };

  const getFieldName = (fieldId) => {
    const field = fields.find((f) => f.id === fieldId);
    return field ? `${field.name} (${field.farm.name})` : "-";
  };

  const getField = (fieldId) => {
    return fields.find((f) => f.id === fieldId);
  };

  const getSensorHistory = (sensorId) => {
    return installations
      .filter((inst) => inst.sensor_id === sensorId)
      .sort(
        (a, b) => new Date(b.installation_date) - new Date(a.installation_date)
      );
  };

  const getFieldSensors = (fieldId) => {
    const fieldInstallations = installations.filter(
      (inst) => inst.field_id === fieldId
    );
    return fieldInstallations
      .map((inst) => {
        const sensor = sensors.find((s) => s.id === inst.sensor_id);
        return {
          ...inst,
          sensor: sensor,
        };
      })
      .sort(
        (a, b) => new Date(b.installation_date) - new Date(a.installation_date)
      );
  };

  // Improved filter logic
  const getFilteredSensors = () => {
    let filtered = [...sensors];

    // Search filter
    if (filters.search) {
      filtered = filtered.filter(
        (sensor) =>
          (sensor.code || "")
            .toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          (sensor.sensorName || "")
            .toLowerCase()
            .includes(filters.search.toLowerCase())
      );
    }

    // Status filter
    if (filters.status === "assigned") {
      filtered = filtered.filter((sensor) => sensor.field_id);
    } else if (filters.status === "unassigned") {
      filtered = filtered.filter((sensor) => !sensor.field_id);
    }

    // Farm filter
    if (filters.farm) {
      const farmFieldIds = fields
        .filter((f) => f.farm_id === filters.farm)
        .map((f) => f.id);
      filtered = filtered.filter(
        (sensor) => sensor.field_id && farmFieldIds.includes(sensor.field_id)
      );
    }

    // Field filter
    if (filters.field) {
      filtered = filtered.filter((sensor) => sensor.field_id === filters.field);
    }

    return filtered;
  };

const getFilteredFieldsForAssignment = () => {
  if (!assignData.farm_id) return [];
  
  const farmId = assignData.farm_id;
  
  // Option 1: Try to get fields from the flat fields array
  const fieldsFromState = fields.filter(f => {
    // Handle both string and number comparison
    return String(f.farm_id) === String(farmId);
  });
  
  // Option 2: If no fields found, get from nested farms.fields
  if (fieldsFromState.length === 0) {
    const selectedFarm = farms.find(f => String(f.id) === String(farmId));
    return selectedFarm?.fields || [];
  }
  
  return fieldsFromState;
};
  // Pagination
const getPaginatedData = () => {
  const filtered = viewMode === "sensors" ? getFilteredSensors() : getFilteredFields();
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return filtered.slice(startIndex, endIndex);
};

  const totalPages = Math.ceil(
  (viewMode === "sensors" ? getFilteredSensors() : getFilteredFields()).length /
    itemsPerPage
);
  // Statistics
  const getStatistics = () => {
    const totalSensors = sensors.length;
    const assignedSensors = sensors.filter((s) => s.field_id).length;
    const unassignedSensors = totalSensors - assignedSensors;
    const activeInstallations = installations.filter(
      (i) => !i.removal_date
    ).length;
    const fieldsWithSensors = new Set(
      installations.filter((i) => !i.removal_date).map((i) => i.field_id)
    ).size;

    return {
      totalSensors,
      assignedSensors,
      unassignedSensors,
      activeInstallations,
      fieldsWithSensors,
    };
  };

  const stats = getStatistics();

  // Open assign modal
  const handleAssignSensor = (sensor) => {
    setSelectedSensor(sensor);
    const currentField = fields.find((f) => f.id === sensor.field_id);

    setAssignData({
      sensor_id: sensor.id,
      user_id: sensor.user_id || "",
      farm_id: currentField?.farm_id || "",
      field_id: sensor.field_id || "",
      zone_id: sensor.zone_id || "",
      installation_date: new Date().toISOString().split("T")[0],
    });
    setShowAssignModal(true);
  };

  // FIXED: Execute assign with proper API call
  const executeAssign = async () => {
    if (!assignData.field_id) {
      swal({
        icon: "error",
        title: t("error"),
        text: t("select_field_required"),
      });
      return;
    }

    try {
      const field = getField(assignData.field_id);
      const currentAssignment = getCurrentAssignment(assignData.sensor_id);

      // Remove old assignment if exists
      if (
        currentAssignment &&
        currentAssignment.field_id !== assignData.field_id
      ) {
        await api.put(`/sensor-installation/${currentAssignment.id}`, {
          ...currentAssignment,
          removal_date: new Date().toISOString().split("T")[0],
        });
      }

      // Create new installation
      if (
        !currentAssignment ||
        currentAssignment.field_id !== assignData.field_id
      ) {
        await api.post("/sensor-installation", {
          field_id: assignData.field_id,
          sensor_id: assignData.sensor_id,
          installation_date: assignData.installation_date,
          removal_date: null,
        });
      }

      // Update sensor table with field, zone, and user
    const sensorToUpdate = sensors.find(s => s.id === assignData.sensor_id);
    const selectedUser = users.find(u => u.id === Number(assignData.user_id));
    
    await api.post('/sensor/edit-sensor', {
      code: sensorToUpdate.code,
      user_uid: selectedUser.uid, // Backend expects user_uid (maps to users.uid)
      sensor_uid: sensorToUpdate.uid,
      field_id: assignData.field_id,
      zone_id: assignData.zone_id || field?.zone_id || null
    });

      swal({
        icon: "success",
        title: t("success"),
        text: t("sensor_assigned_success"),
      });

      setShowAssignModal(false);
      await Promise.all([fetchSensors(), fetchInstallations()]);
    } catch (error) {
      console.error("Error assigning sensor:", error);
      swal({
        icon: "error",
        title: t("error"),
        text: error.response?.data?.message || t("error_assigning_sensor"),
      });
    }
  };

  // FIXED: Unassign sensor
  const handleUnassignSensor = async (sensor) => {
    const confirm = await swal({
      title: t("confirm_remove_sensor_title"),
      text: t("confirm_remove_sensor_text", { code: sensor.code }),
      icon: "warning",
      buttons: true,
    });

    if (confirm) {
      try {
        const currentAssignment = getCurrentAssignment(sensor.id);

        if (currentAssignment) {
          await api.put(`/sensor-installation/${currentAssignment.id}`, {
            ...currentAssignment,
            removal_date: new Date().toISOString().split("T")[0],
          });
        }

    await api.post('/sensor/retirer-sensor', {
  sensor_uid: sensor.uid,
  code: sensor.code,
});


        swal({
          icon: "success",
          title: t("success"),
          text: t("sensor_removed_success"),
        });

        await Promise.all([fetchSensors(), fetchInstallations()]);
      } catch (error) {
        console.error("Error unassigning sensor:", error);
        swal({
          icon: "error",
          title: t("error"),
          text: t("error_removing_sensor"),
        });
      }
    }
  };

  const handleViewHistory = (sensor) => {
    setSelectedSensor(sensor);
    setSensorHistory(getSensorHistory(sensor.id));
    setShowHistoryModal(true);
  };

  // Simplified filter handler
  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      farm: "",
      field: "",
      status: "all",
      search: "",
    });
    setCurrentPage(1);
  };

  // Navigate to field history page
  const handleViewFieldHistory = (field) => {
    navigate(`/field-history/${field.id}`, { state: { field } });
  };

  return (
    <Container fluid className="main-content-container admin-management-page sensor-installation-management-page">
      {/* Page Header */}
      <Row noGutters className="page-header py-4">
        <PageTitle
          title={t("Gestion des Capteurs")}
          subtitle={t("Assigner et gérer les capteurs dans les champs")}
          className="text-sm-left"
        />
      </Row>

      {/* Statistics Cards - Improved Design */}
      <Row className="mb-4 gap-1 d-flex justify-content-around sensor-stats-row">
        <Col lg="3" md="6" className="mb-3">
          <Card className="sensor-stat-card border-0 shadow-sm h-100 ">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">{t("Total Capteurs")}</p>
                  <h2 className="mb-0 fw-bold">{stats.totalSensors}</h2>
                </div>
                <div
                  className="bg-primary bg-opacity-10 rounded-circle p-3"
                  style={{ width: "56px", height: "56px" }}
                >
                  <i className="fas fa-microchip sensor-stat-icon" aria-hidden="true" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg="3" md="6" className="mb-3">
          <Card className="sensor-stat-card border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">{t("Assignés")}</p>
                  <h2 className="mb-0 fw-bold text-success">
                    {stats.assignedSensors}
                  </h2>
                </div>
                <div
                  className="bg-success bg-opacity-10 rounded-circle p-3"
                  style={{ width: "56px", height: "56px" }}
                >
                  <i className="fas fa-link sensor-stat-icon" aria-hidden="true" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg="3" md="6" className="mb-3">
          <Card className="sensor-stat-card border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">{t("Non Assignés")}</p>
                  <h2 className="mb-0 fw-bold text-warning">
                    {stats.unassignedSensors}
                  </h2>
                </div>
                <div
                  className="bg-warning bg-opacity-10 rounded-circle p-3"
                  style={{ width: "56px", height: "56px" }}
                >
                  <i className="fas fa-triangle-exclamation sensor-stat-icon" aria-hidden="true" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* View Mode Tabs - Improved */}
    <Card className="sensor-content-card mb-4 border-0 shadow-sm">
  <Card.Body className="p-0">
    <div className="d-flex justify-content-between align-items-center p-3 sensor-view-toolbar">
      <Nav variant="pills" activeKey={viewMode}>
        <Nav.Item>
          <Nav.Link
            eventKey="sensors"
            onClick={() => setViewMode("sensors")}
            className="me-2"
          >
            <i className="material-icons align-middle me-1">&#xe1b8;</i>
            {t("Vue par Capteurs")}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            eventKey="fields"
            onClick={() => setViewMode("fields")}
          >
            <i className="material-icons align-middle me-1">&#xe55d;</i>
            {t("Vue par Champs")}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Button
        variant="outline-primary"
        onClick={() => navigate("/admin/add-sensor")}
      >
        {t("Add Sensor")}
      </Button>
    </div>
  </Card.Body>
</Card>


      {/* IMPROVED: Simplified Filters */}
      {viewMode === "sensors" && (
        <Card className="sensor-content-card sensor-filter-card mb-4 border-0 shadow-sm">
          <Card.Body>
            <Row className="g-3">
              {/* Search - Full width on mobile */}
              <Col lg="12" className="mb-2 mb-lg-0">
                <InputGroup>
                  <InputGroup.Text className="bg-white">
                    <i className="material-icons text-muted">&#xe8b6;</i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder={t("Rechercher par code ou nom de capteur...")}
                    value={filters.search}
                    onChange={(e) => updateFilter("search", e.target.value)}
                    style={{ borderLeft: "none" }}
                  />
                  {filters.search && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => updateFilter("search", "")}
                      aria-label={t("reset")}
                      title={t("reset")}
                    >
<i className="fas fa-xmark" aria-hidden="true" />
                    </Button>
                  )}
                </InputGroup>
              </Col>

              {/* Quick Filters */}
              <Col lg="3" md="4" sm="6">
                <InputGroup size="sm" className="sensor-filter-group">
                  <InputGroup.Text>
                    <i className="fas fa-filter" aria-hidden="true" />
                  </InputGroup.Text>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => updateFilter("status", e.target.value)}
                  >
                    <option value="all">{t("Tous les statuts")}</option>
                    <option value="assigned">{t("Assignés uniquement")}</option>
                    <option value="unassigned">{t("Non assignés")}</option>
                  </Form.Select>
                </InputGroup>
              </Col>

              <Col lg="4" md="4" sm="6">
                <InputGroup size="sm" className="sensor-filter-group">
                  <InputGroup.Text>
                    <i className="fas fa-warehouse" aria-hidden="true" />
                  </InputGroup.Text>
                  <Form.Select
                    value={filters.farm}
                    onChange={(e) => {
                      updateFilter("farm", e.target.value);
                      updateFilter("field", "");
                    }}
                  >
                    <option value="">{t("Toutes les fermes")}</option>
                    {farms.map((farm) => (
                      <option key={farm.id} value={farm.id}>
                        {farm.name}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </Col>

              <Col lg="4" md="4" sm="6">
                <InputGroup size="sm" className="sensor-filter-group">
                  <InputGroup.Text>
                    <i className="fas fa-seedling" aria-hidden="true" />
                  </InputGroup.Text>
                  <Form.Select
                    value={filters.field}
                    onChange={(e) => updateFilter("field", e.target.value)}
                    disabled={!filters.farm}
                  >
                    <option value="">{t("Tous les champs")}</option>
                    {fields
                      .filter((f) => !filters.farm || f.farm_id === filters.farm)
                      .map((field) => (
                        <option key={field.id} value={field.id}>
                          {field.name}
                        </option>
                      ))}
                  </Form.Select>
                </InputGroup>
              </Col>

              <Col lg="1" className="text-end">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                  className="w-100 sensor-filter-reset"
                  aria-label={t("reset")}
                  title={t("reset")}
                >
                  <i className="fas fa-xmark" aria-hidden="true" />
                </Button>
              </Col>
            </Row>

            {/* Active Filters Display */}
            {(filters.search ||
              filters.status !== "all" ||
              filters.farm ||
              filters.field) && (
              <Row className="mt-3">
                <Col>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <small className="text-muted">{t("Filtres actifs:")}</small>
                    {filters.search && (
                      <Badge bg="primary" className="d-flex align-items-center">
                        {t('Recherche: "{{query}}"', { query: filters.search })}
                        <i
                          className="material-icons ms-1"
                          style={{
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                          onClick={() => updateFilter("search", "")}
                        >
                          &#xe5cd;
                        </i>
                      </Badge>
                    )}
                    {filters.status !== "all" && (
                      <Badge bg="success" className="d-flex align-items-center">
                        {filters.status === "assigned"
                          ? t("Assignés")
                          : t("Non assigné")}
                        <i
                          className="material-icons ms-1"
                          style={{
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                          onClick={() => updateFilter("status", "all")}
                        >
                          &#xe5cd;
                        </i>
                      </Badge>
                    )}
                    {filters.farm && (
                      <Badge bg="info" className="d-flex align-items-center">
                        {t("Ferme")}:{" "}
                        {farms.find((f) => f.id === filters.farm)?.name}
                        <i
                          className="material-icons ms-1"
                          style={{
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            updateFilter("farm", "");
                            updateFilter("field", "");
                          }}
                        >
                          &#xe5cd;
                        </i>
                      </Badge>
                    )}
                    {filters.field && (
                      <Badge bg="warning" className="d-flex align-items-center">
                        {t("Champ")}:{" "}
                        {fields.find((f) => f.id === filters.field)?.name}
                        <i
                          className="material-icons ms-1"
                          style={{
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                          onClick={() => updateFilter("field", "")}
                        >
                          &#xe5cd;
                        </i>
                      </Badge>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      )}
{/* FIELDS VIEW FILTERS */}
{viewMode === "fields" && (
  <Card className="sensor-content-card sensor-filter-card mb-4 border-0 shadow-sm">
    <Card.Body>
      <Row className="g-3">
        {/* Search - Full width on mobile */}
        <Col lg="12" className="mb-2 mb-lg-0">
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <i className="material-icons text-muted">&#xe8b6;</i>
            </InputGroup.Text>
            <Form.Control
              placeholder={t("Rechercher par nom de champ ou ferme...")}
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              style={{ borderLeft: "none" }}
            />
            {filters.search && (
              <Button
                variant="outline-secondary"
                onClick={() => updateFilter("search", "")}
                aria-label={t("reset")}
                title={t("reset")}
              >
<i className="fas fa-xmark" aria-hidden="true" />
              </Button>
            )}
          </InputGroup>
        </Col>
      </Row>

      {/* Active Filters Display */}
      {(filters.search ||
        filters.farm ||
        filters.fieldsWithSensors) && (
        <Row className="mt-3">
          <Col>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <small className="text-muted">{t("Filtres actifs:")}</small>
              {filters.search && (
                <Badge bg="primary" className="d-flex align-items-center">
                  {t('Recherche: "{{query}}"', { query: filters.search })}
                  <i
                    className="material-icons ms-1"
                    style={{
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                    onClick={() => updateFilter("search", "")}
                  >
                    &#xe5cd;
                  </i>
                </Badge>
              )}
              {filters.farm && (
                <Badge bg="info" className="d-flex align-items-center">
                  {t("Ferme")}: {farms.find((f) => f.id === filters.farm)?.name}
                  <i
                    className="material-icons ms-1"
                    style={{
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                    onClick={() => updateFilter("farm", "")}
                  >
                    &#xe5cd;
                  </i>
                </Badge>
              )}
              {filters.fieldsWithSensors && (
                <Badge bg="success" className="d-flex align-items-center">
                  {t("with_active_sensors")}
                  <i
                    className="material-icons ms-1"
                    style={{
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                    onClick={() => updateFilter("fieldsWithSensors", false)}
                  >
                    &#xe5cd;
                  </i>
                </Badge>
              )}
            </div>
          </Col>
        </Row>
      )}
    </Card.Body>
  </Card>
)}
      {/* Results Count */}
    <div className="d-flex justify-content-between align-items-center mb-3">
  <span className="text-muted">
    <strong>
      {viewMode === "sensors"
        ? getFilteredSensors().length
        : getFilteredFields().length}
    </strong>{" "}
    {t("résultat(s)")}
  </span>
  {totalPages > 1 && (
    <small className="text-muted">
      {t("Page")} {currentPage} / {totalPages}
    </small>
  )}
</div>

      {/* Main Content Table */}
      <Row>
        <Col lg="12">
          <Card className="sensor-content-card border-0 shadow-sm">
            <Card.Body className="p-0">
              {loading ? (
                  <div className="sensor-empty-state text-center p-5">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ width: "3rem", height: "3rem" }}
                  >
                    <span className="visually-hidden">{t("Chargement...")}</span>
                  </div>
                  <p className="text-muted mt-3">{t("Chargement des données...")}</p>
                </div>
              ) : viewMode === "sensors" ? (
                /* SENSORS VIEW */
                <>
                  <div className="table-responsive sensor-table-wrap">
                    <Table hover className="sensor-table mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0">{t("Code Capteur")}</th>
                          <th className="border-0">{t("Nom")}</th>
                          <th className="border-0">{t("Champ Actuel")}</th>
                          <th className="border-0">{t("Zone")}</th>
                          <th className="border-0">{t("Date Installation")}</th>
                          <th className="border-0">{t("Statut")}</th>
                          <th className="border-0 text-end">{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedData().length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <i
                                className="material-icons text-muted mb-2"
                                style={{ fontSize: "48px" }}
                              >
                                &#xe002;
                              </i>
                              <p className="text-muted">{t("Aucun capteur trouvé")}</p>
                            </td>
                          </tr>
                        ) : (
                          getPaginatedData().map((sensor) => {
                            const currentAssignment = getCurrentAssignment(
                              sensor.id
                            );
                            const isAssigned = !!sensor.field_id;

                            return (
                              <tr key={sensor.id}>
                                <td>
                                  <Badge bg="dark" className="font-monospace">
                                    {sensor.code}
                                  </Badge>
                                </td>
                                <td>
                                  <div>
                                    <div>{sensor.sensorName || "-"}</div>
                                    {sensor.lastReadingTime && (
                                      <small className="text-muted">
                                        {t("Dernière lecture:")}{" "}
                                        {new Date(
                                          sensor.lastReadingTime
                                        ).toLocaleDateString()}
                                      </small>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  {isAssigned ? (
                                    <span className="text-success fw-semibold">
                                      {getFieldName(sensor.field_id)}
                                    </span>
                                  ) : (
                                    <span className="text-muted">
                                      {t("Non assigné")}
                                    </span>
                                  )}
                                </td>
                                <td>{sensor.zone_id || "-"}</td>
                                <td>
                                  {currentAssignment?.installation_date || "-"}
                                </td>
                                <td>
                                  <Badge
                                    bg={isAssigned ? "success" : "warning"}
                                    className="px-3 d-inline-flex align-items-center gap-1"
                                  >
                                    <i className={`fas ${isAssigned ? "fa-check" : "fa-triangle-exclamation"}`} aria-hidden="true" />
                                    {isAssigned ? t("Assigné") : t("Libre")}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="d-flex justify-content-end gap-1">
                                    <Button
                                      variant={
                                        isAssigned
                                          ? "outline-primary"
                                          : "primary"
                                      }
                                      size="sm"
                                      onClick={() => handleAssignSensor(sensor)}
                                      title={isAssigned ? t("Réassigner") : t("Assigner")}
                                    >
                                   <i className={`fas ${isAssigned ? "fa-check" : "fa-plus"}`} aria-hidden="true" />

                                    </Button>
                                    {isAssigned && (
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() =>
                                          handleUnassignSensor(sensor)
                                        }
                                        title={t("Retirer")}
                                      >
                                        <i className="fas fa-link-slash" aria-hidden="true" />
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={() => handleViewHistory(sensor)}
                                      title={t("Historique")}
                                    >
                                      <i className="fas fa-clock-rotate-left" aria-hidden="true" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center p-3 border-top sensor-pagination">
        <small className="text-muted">
  {t("Affichage")} {(currentPage - 1) * itemsPerPage + 1} -{" "}
  {Math.min(
    currentPage * itemsPerPage,
    getFilteredFields().length
  )}{" "}
  {t("sur")} {getFilteredFields().length}
</small>
                      <ButtonGroup size="sm">
                        <Button
                          variant="outline-primary"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(1)}
                        >
                          <i className="material-icons">&#xe5dc;</i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          <i className="material-icons">&#xe5cb;</i>
                        </Button>
                        <Button variant="outline-secondary" disabled>
                          {currentPage} / {totalPages}
                        </Button>
                        <Button
                          variant="outline-primary"
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          <i className="material-icons">&#xe5cc;</i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          <i className="material-icons">&#xe5dd;</i>
                        </Button>
                      </ButtonGroup>
                    </div>
                  )}
                </>
              ) : (
                /* FIELDS VIEW */
                <>
                {viewMode === "fields" && (
  <Card className="mb-4 border-0 shadow-sm">
    <Card.Body>
      <Row className="g-3">
        <Col lg="8" md="6">
          <Form.Check
            type="switch"
            id="fields-with-sensors-filter"
            label={t("Afficher uniquement les champs avec capteurs actifs")}
            checked={filters.fieldsWithSensors}
            onChange={(e) => updateFilter("fieldsWithSensors", e.target.checked)}
          />
        </Col>
        <Col lg="4" md="6" className="text-end">
          {filters.fieldsWithSensors && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => updateFilter("fieldsWithSensors", false)}
            >
              <i className="material-icons">&#xe14c;</i> {t("Réinitialiser")}
            </Button>
          )}
        </Col>
      </Row>
    </Card.Body>
  </Card>
)}
                  <div className="table-responsive sensor-table-wrap">
                    <Table hover className="sensor-table mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0">{t("Champ")}</th>
                          <th className="border-0">{t("Ferme")}</th>
                          <th className="border-0">{t("Capteurs Actifs")}</th>
                          <th className="border-0">{t("Historique Total")}</th>
                          <th className="border-0 text-end">{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedData().length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center py-5">
                              <i
                                className="material-icons text-muted mb-2"
                                style={{ fontSize: "48px" }}
                              >
                                &#xe002;
                              </i>
                              <p className="text-muted">{t("Aucun champ trouvé")}</p>
                            </td>
                          </tr>
                        ) : (
                          getPaginatedData().map((field) => {
                            const fieldSensors = getFieldSensors(field.id);
                            const activeSensors = fieldSensors.filter(
                              (fs) => !fs.removal_date
                            );

                            return (
                              <tr key={field.id}>
                                <td>
                                  <div>
                                    <strong>{field.name}</strong>
                                    {field.area && (
                                      <div className="small text-muted">
                                        {field.area} {t("ha")}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>{field.farm.name}</td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <Badge
                                      bg={
                                        activeSensors.length > 0
                                          ? "success"
                                          : "secondary"
                                      }
                                    >
                                      {activeSensors.length}
                                    </Badge>
                                    {activeSensors.length > 0 && (
                                      <div className="d-flex gap-1 flex-wrap">
                                        {activeSensors.map((fs) => (
                                          <Badge
                                            key={fs.id}
                                            bg="dark"
                                            className="font-monospace"
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            {fs.sensor?.code}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <span className="text-muted">
                                    {fieldSensors.length} {t("installation(s)")}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex justify-content-end gap-1">
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedSensor({
                                          field_name: field.name,
                                        });
                                        setSensorHistory(fieldSensors);
                                        setShowHistoryModal(true);
                                      }}
                                      title={t("Historique des installations")}
                                    >
                                      <i className="fas fa-clock-rotate-left" aria-hidden="true" />{" "}
                                      {t("Installations")}
                                    </Button>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() =>
                                        handleViewFieldHistory(field)
                                      }
                                      title={t("view_readings_history")}
                                    >
                                      <i className="fas fa-chart-line" aria-hidden="true" />{" "}
                                      {t("Lectures")}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination for Fields */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center p-3 border-top sensor-pagination">
                      <small className="text-muted">
                        {t("Page")} {currentPage} {t("sur")} {totalPages}
                      </small>
                      <ButtonGroup size="sm">
                        <Button
                          variant="outline-primary"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          <i className="material-icons">&#xe5cb;</i> {t("Précédent")}
                        </Button>
                        <Button
                          variant="outline-primary"
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          {t("Suivant")} <i className="material-icons">&#xe5cc;</i>
                        </Button>
                      </ButtonGroup>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Assign/Reassign Modal - Improved */}
      <Modal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        size="lg"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <i className={`fas ${selectedSensor?.field_id ? "fa-check-circle" : "fa-plus"} align-middle me-2`} aria-hidden="true" />

            {selectedSensor?.field_id ? t("Réassigner") : t("Assigner")} {t("le Capteur")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="d-flex align-items-start">
            <i className="fas fa-circle-info me-2" aria-hidden="true" />
            <div>
              <strong>{t("Information:")}</strong> {t("L'assignation mettra à jour automatiquement le capteur, créera un historique d'installation et retirera toute assignation précédente si nécessaire.")}
            </div>
          </Alert>

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="fas fa-microchip align-middle me-1" aria-hidden="true" />
                    {t("Capteur")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedSensor?.code || ""}
                    disabled
                    className="bg-light"
                  />
                </Form.Group>
              </Col>

              {selectedSensor?.field_id && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-map align-middle me-1" aria-hidden="true" />
                      {t("Champ Actuel")}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={getFieldName(selectedSensor.field_id)}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            <hr />

            <h6 className="mb-3 text-primary">
              <i className="material-icons align-middle me-1">&#xe3c9;</i>
              {t("Nouvelle Assignation")}
            </h6>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <i className="material-icons align-middle me-1">&#xe7fd;</i>
                {t("Utilisateur *")}
              </Form.Label>
              <Form.Select
                value={assignData.user_id}
                onChange={(e) =>
                  setAssignData({
                    ...assignData,
                    user_id: e.target.value,
                    farm_id: "",
                    field_id: "",
                    zone_id: "",
                  })
                }
                required
              >
                <option value="">{t("-- Sélectionner un utilisateur --")}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name || user.email}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <i className="material-icons align-middle me-1">&#xe0af;</i>
                {t("Ferme *")}
              </Form.Label>
              <Form.Select
                value={assignData.farm_id}
                onChange={(e) =>
                  setAssignData({
                    ...assignData,
                    farm_id: e.target.value,
                    field_id: "",
                    zone_id: "",
                  })
                }
                disabled={!assignData.user_id}
                required
              >
                <option value="">{t("-- Sélectionner une ferme --")}</option>
                {getFilteredFarms().map((farm) => (
                  <option key={farm.id} value={farm.id}>
                    {farm.name}
                  </option>
                ))}
              </Form.Select>
              {!assignData.user_id && (
                <Form.Text className="text-muted">
                  <i className="fas fa-circle-info me-1" aria-hidden="true" />
                  {t("Sélectionnez d'abord un utilisateur")}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <i className="material-icons align-middle me-1">&#xe55d;</i>
                {t("Champ *")}
              </Form.Label>
<Form.Select
  value={assignData.field_id}
  onChange={(e) =>
    setAssignData({
      ...assignData,
      field_id: e.target.value,
      zone_id: "",
    })
  }
  disabled={!assignData.farm_id}
  required
>
  <option value="">{t("-- Sélectionner un champ --")}</option>
  {getFilteredFieldsForAssignment().map((field) => (
    <option key={field.id} value={field.id}>
      {field.name}
    </option>
  ))}
</Form.Select>
              {!assignData.farm_id && (
                <Form.Text className="text-muted">
                  <i className="fas fa-circle-info me-1" aria-hidden="true" />
                  {t("Sélectionnez d'abord une ferme")}
                </Form.Text>
              )}
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="fas fa-draw-polygon align-middle me-1" aria-hidden="true" />
                    {t("Zone (Optionnel)")}
                  </Form.Label>
                  <Form.Select
                    value={assignData.zone_id}
                    onChange={(e) =>
                      setAssignData({
                        ...assignData,
                        zone_id: e.target.value,
                      })
                    }
                    disabled={!assignData.field_id}
                  >
                    <option value="">{t("-- Aucune zone spécifique --")}</option>
                    {getFilteredZones().map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="fas fa-calendar-day align-middle me-1" aria-hidden="true" />
                    {t("Date d'Installation *")}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={assignData.installation_date}
                    onChange={(e) =>
                      setAssignData({
                        ...assignData,
                        installation_date: e.target.value,
                      })
                    }
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setShowAssignModal(false)}
          >
            <i className="fas fa-xmark align-middle me-1" aria-hidden="true" />
            {t("Annuler")}
          </Button>
          <Button
            variant="success"
            onClick={executeAssign}
            disabled={!assignData.field_id}
          >
            <i className="fas fa-check align-middle me-1" aria-hidden="true" />
            {t("Confirmer l'Assignation")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* History Modal - Improved */}
      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        size="lg"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <i className="material-icons align-middle me-2">&#xe889;</i>
            {t("Historique des Installations -")}{" "}
            {selectedSensor?.code || selectedSensor?.field_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sensorHistory.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="material-icons text-muted mb-3"
                style={{ fontSize: "64px" }}
              >
                &#xe88f;
              </i>
              <h5 className="text-muted">{t("Aucun historique disponible")}</h5>
              <p className="text-muted">
                {t("Ce capteur n'a pas encore été installé dans un champ.")}
              </p>
            </div>
          ) : (
            <div className="table-responsive sensor-table-wrap">
              <Table hover className="sensor-table mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>{t("Champ")}</th>
                    <th>{t("Capteur")}</th>
                    <th>{t("Date Installation")}</th>
                    <th>{t("Date Retrait")}</th>
                    <th>{t("Durée")}</th>
                    <th>{t("Statut")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sensorHistory.map((hist) => {
                    const installDate = new Date(hist.installation_date);
                    const removeDate = hist.removal_date
                      ? new Date(hist.removal_date)
                      : new Date();
                    const duration = Math.floor(
                      (removeDate - installDate) / (1000 * 60 * 60 * 24)
                    );

                    return (
                      <tr key={hist.id}>
                        <td>
                          <strong>{getFieldName(hist.field_id)}</strong>
                        </td>
                        <td>
                          <Badge bg="dark" className="font-monospace">
                            {hist.sensor?.code ||
                              sensors.find((s) => s.id === hist.sensor_id)
                                ?.code}
                          </Badge>
                        </td>
                        <td>
                            <i className="fas fa-calendar-day align-middle me-1 text-muted" aria-hidden="true" />
                          {installDate.toLocaleDateString("fr-FR")}
                        </td>
                        <td>
                          {hist.removal_date ? (
                            <>
                              <i className="fas fa-calendar-day align-middle me-1 text-muted" aria-hidden="true" />
                              {new Date(hist.removal_date).toLocaleDateString(
                                "fr-FR"
                              )}
                            </>
                          ) : (
                            <span className="text-success fw-semibold">
                              {t("En cours")}
                            </span>
                          )}
                        </td>
                        <td>
                          <Badge bg="info">{duration} {t("jour(s)")}</Badge>
                        </td>
                        <td>
                          <Badge
                            bg={hist.removal_date ? "secondary" : "success"}
                            className="d-inline-flex align-items-center gap-1"
                          >
                            {!hist.removal_date && (
                              <i className="fas fa-check" aria-hidden="true" />
                            )}
                            {hist.removal_date ? t("Terminé") : t("Actif")}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowHistoryModal(false)}>
            <i className="fas fa-xmark align-middle me-1" aria-hidden="true" />
            {t("Fermer")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SensorInstallationManager;
